import { Separator } from '@qr-front/ui/components/Separator'
import { cn, getVersionForClone } from '@qr-front/ui/utils'
import * as Accordion from '@radix-ui/react-accordion'
import {
	KeyboardEvent,
	ReactElement,
	ReactNode,
	SVGProps,
	SyntheticEvent,
	useRef,
} from 'react'
import AnimateHeight from 'react-animate-height'

import { captureBuilderEvent, STEP_TO_INDEX } from '../builderAnalytics'
import { useCurrGeneratorStep } from '../hooks/useCurrGeneratorStep'
import {
	advanceFromField,
	applyEnterKeyHint,
	isEnterNavigable,
} from './sectionEnterNavigation'

export const GENERATOR_FORM_ANIMATION_DURATION = 300

export type TGeneratorFormSection = {
	id: string
	title: string
	description: string
	icon: ReactElement
	disabled?: boolean
	Content: () => ReactNode
}

const IconChevronDown = (props: SVGProps<SVGSVGElement>) => (
	<svg
		width={32}
		height={24}
		viewBox="0 0 32 24"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		{...props}
	>
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M9.575 8.66a1.25 1.25 0 0 1 1.766-.085L16 12.811l4.66-4.236a1.25 1.25 0 1 1 1.68 1.85l-5.5 5a1.25 1.25 0 0 1-1.68 0l-5.5-5a1.25 1.25 0 0 1-.085-1.766"
			fill="#000"
		/>
	</svg>
)

const IconContainer = ({ icon }: { icon: ReactElement }) => (
	<div className="hidden size-12 items-center justify-center rounded-8 bg-light-grey-70 text-blue tablet:flex [&_svg]:size-6">
		{icon}
	</div>
)

export type TGeneratorFormSectionProps = {
	id: string
	title: string
	description: string
	icon: ReactElement
	content: ReactNode
	isOpen: boolean
	disabled?: boolean
}

export const GeneratorFormSection = ({
	id,
	title,
	description,
	icon,
	content,
	isOpen,
	disabled,
}: TGeneratorFormSectionProps) => {
	const headerRef = useRef<HTMLButtonElement>(null)
	const currStep = useCurrGeneratorStep()

	const handleEnterKeyHint = (event: SyntheticEvent<HTMLDivElement>) => {
		if (isEnterNavigable(event.target, event.currentTarget)) {
			applyEnterKeyHint(event.target, event.currentTarget)
		}
	}

	const handleContentKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		if (
			event.key !== 'Enter' ||
			event.nativeEvent.isComposing ||
			!isEnterNavigable(event.target, event.currentTarget)
		) {
			return
		}
		event.preventDefault()
		advanceFromField(event.target, event.currentTarget)
	}

	const header = (
		<Accordion.Header asChild>
			<Accordion.Trigger
				ref={headerRef}
				disabled={disabled}
				onClick={() => {
					if (!isOpen) {
						// MYQRCODE-10778 AC#13: id is this section's own internal
						// identifier (e.g. 'about', 'menu') — the closest string-typed
						// stand-in for the registry's accordion_name on QR types that
						// have no vCard-style nested sub-accordions.
						captureBuilderEvent('builder_open_accordion', {
							accordion_name: id,
							step_index: STEP_TO_INDEX[currStep],
							section: 'wizard_content',
						})
						setTimeout(() => {
							headerRef.current?.scrollIntoView({
								behavior: 'smooth',
								block: 'start',
							})
						}, GENERATOR_FORM_ANIMATION_DURATION * 1.25)
					}
				}}
				className={cn(
					'relative w-full scroll-mt-20 p-4 text-start transition-opacity duration-300 tablet:px-8 desktop:scroll-mt-22 [&[data-state=open]>svg]:rotate-180',
					disabled && 'opacity-50',
				)}
			>
				<div className="flex gap-4">
					{getVersionForClone({
						base: <IconContainer icon={icon} />,
						clone1: null,
					})}
					<div className="pr-10">
						<h2 className="type-heading-4">{title}</h2>
						<p
							className={cn(
								'type-body-small text-dark-grey',
								getVersionForClone({
									base: '',
									clone1: 'tracking-wide',
								}),
							)}
						>
							{description}
						</p>
					</div>
				</div>
				<IconChevronDown className="absolute right-4 top-4 transition-transform duration-200 tablet:right-8 tablet:-mr-2" />
			</Accordion.Trigger>
		</Accordion.Header>
	)

	return (
		<Accordion.Item
			value={id}
			className="group rounded-10 bg-white shadow-generator"
		>
			{header}
			<Separator
				collapse
				className="mx-4 transition-opacity duration-200 group-data-[state=closed]:opacity-0 tablet:mx-8"
			/>
			<Accordion.Content forceMount>
				<AnimateHeight
					height={isOpen ? 'auto' : 0}
					disableDisplayNone
					duration={GENERATOR_FORM_ANIMATION_DURATION}
					contentClassName="overflow-hidden relative"
				>
					<div
						className="p-4 pb-6 pt-6 tablet:p-8"
						onPointerDownCapture={handleEnterKeyHint}
						onFocusCapture={handleEnterKeyHint}
						onKeyDown={handleContentKeyDown}
					>
						{content}
					</div>
				</AnimateHeight>
			</Accordion.Content>
		</Accordion.Item>
	)
}
